import React from "react";
import PropTypes from "prop-types";

import useMediaQuery from "@mui/material/useMediaQuery";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";

import {
  useDefaultPasscodeStyles,
  switchControl,
  textfield,
  selectField,
  getSelectValues,
  getSelectedValueTranslated,
  getKeyFromSelectedValue,
} from "../../../constants/passCodeUtils";
import { MIN_DESKTOP_WIDTH } from "../../../constants/ui";

import {
  HOST_ENTRANCE_TONE,
  HOST_EXIT_TONE,
  HOST_PASSCODE,
  HOST_CODE_LENGTH,
  PASSCODE_RELOAD_INTERVAL,
  HOST_CUSTOM_CONFIRM_MESSAGE,
  HOST_TALK_MODE,
  ALLOW_HOST_MUTE_OVERRIDE,
  GUEST_ENTRANCE_TONE,
  GUEST_EXIT_TONE,
  GUEST_PASSCODE,
  GUEST_CODE_LENGTH,
  GUEST_CUSTOM_CONFIRM_MESSAGE,
  GUEST_TALK_MODE,
  ALLOW_GUEST_MUTE_OVERRIDE,
  PARAM_HOST_CODE_LENGTH,
  PARAM_GUEST_CODE_LENGTH,
  PARAM_CUSTOM_CONFIRM_MESSAGE,
} from "../../../utils/responseValue";

const readOnlyFieldSx = theme => ({
  backgroundColor: "#ffffff",
  borderRadius: "5px",
  m: 1,
});

const headerSx = theme => ({
  fontSize: "32px",
  p: 1,
});

const selectPaddingSx = theme => ({ p: 1 });

const PMHostAndGuest = ({ confRestRep, setConfRestRep, selectValues, translate }) => {
  const pc = useDefaultPasscodeStyles();
  const isDesktop = useMediaQuery(`(min-width:${MIN_DESKTOP_WIDTH})`);

  const handleTextChange = name => event => {
    setConfRestRep({ ...confRestRep, [name]: event.target.value });
  };
  const handleSwitchChange = name => event => {
    setConfRestRep({ ...confRestRep, [name]: event.target.checked });
  };
  const handleSelectChange = (name, confKey) => event => {
    setConfRestRep({
      ...confRestRep,
      [confKey || name]: getKeyFromSelectedValue(selectValues[name], event.target.value),
    });
  };

  const customStyle = theme => ({
    formControl: {
      width: "200px",
    },
  });
  return (
    <Box component="form" sx={pc.container} noValidate autoComplete="off">
      <Box sx={pc.centerer}>
        <Box sx={headerSx}>Host</Box>
        <Box sx={pc.lineBreak} />
        {selectField(
          customStyle,
          translate[HOST_ENTRANCE_TONE],
          getSelectValues(selectValues, HOST_ENTRANCE_TONE),
          getSelectedValueTranslated(confRestRep[HOST_ENTRANCE_TONE], selectValues, HOST_ENTRANCE_TONE),
          handleSelectChange(HOST_ENTRANCE_TONE),
        isDesktop
        )}
        {selectField(
          customStyle,
          translate[HOST_EXIT_TONE],
          getSelectValues(selectValues, HOST_EXIT_TONE),
          getSelectedValueTranslated(confRestRep[HOST_EXIT_TONE], selectValues, HOST_EXIT_TONE),
          handleSelectChange(HOST_EXIT_TONE),
        isDesktop
        )}
      </Box>
      <Box sx={pc.lineBreak} />
      <TextField
        label={translate[HOST_PASSCODE]}
        sx={[pc.textField, isDesktop ? pc.lineExpander : null, readOnlyFieldSx].filter(Boolean)}
        value={confRestRep[HOST_PASSCODE] || ""}
        margin="normal"
        slotProps={{
          input: {
            readOnly: true,
          },
        }}
      />

      {selectField(
        customStyle,
        translate[HOST_CODE_LENGTH],
        getSelectValues(selectValues, PARAM_HOST_CODE_LENGTH),
        getSelectedValueTranslated(confRestRep[HOST_CODE_LENGTH], selectValues, PARAM_HOST_CODE_LENGTH),
        handleSelectChange(PARAM_HOST_CODE_LENGTH, HOST_CODE_LENGTH),
      isDesktop
      )}
      <Box sx={pc.lineBreak} />
      {textfield(
        pc,
        isDesktop,
        confRestRep[PASSCODE_RELOAD_INTERVAL],
        translate[PASSCODE_RELOAD_INTERVAL],
        handleTextChange(PASSCODE_RELOAD_INTERVAL)
      )}
      <Box sx={selectPaddingSx}>
        {selectField(
          customStyle,
          translate[PARAM_CUSTOM_CONFIRM_MESSAGE],
          getSelectValues(selectValues, PARAM_CUSTOM_CONFIRM_MESSAGE),
          getSelectedValueTranslated(
            confRestRep[HOST_CUSTOM_CONFIRM_MESSAGE],
            selectValues,
            PARAM_CUSTOM_CONFIRM_MESSAGE
          ),
          handleSelectChange(PARAM_CUSTOM_CONFIRM_MESSAGE, HOST_CUSTOM_CONFIRM_MESSAGE),
        isDesktop
        )}
      </Box>
      <Box sx={pc.lineBreak} />
      {switchControl(
        pc,
        confRestRep[HOST_TALK_MODE],
        HOST_TALK_MODE,
        translate[HOST_TALK_MODE],
        handleSwitchChange(HOST_TALK_MODE)
      )}
      <Box sx={pc.lineBreak} />
      {switchControl(
        pc,
        confRestRep[ALLOW_HOST_MUTE_OVERRIDE],
        ALLOW_HOST_MUTE_OVERRIDE,
        translate[ALLOW_HOST_MUTE_OVERRIDE],
        handleSwitchChange(ALLOW_HOST_MUTE_OVERRIDE)
      )}
      <Box sx={pc.lineBreak} />
      <Box sx={pc.centerer}>
        <Box sx={headerSx}>Guest</Box>
        <Box sx={pc.lineBreak} />
        {selectField(
          customStyle,
          translate[GUEST_ENTRANCE_TONE],
          getSelectValues(selectValues, GUEST_ENTRANCE_TONE),
          getSelectedValueTranslated(confRestRep[GUEST_ENTRANCE_TONE], selectValues, GUEST_ENTRANCE_TONE),
          handleSelectChange(GUEST_ENTRANCE_TONE),
        isDesktop
        )}
        {selectField(
          customStyle,
          translate[GUEST_EXIT_TONE],
          getSelectValues(selectValues, GUEST_EXIT_TONE),
          getSelectedValueTranslated(confRestRep[GUEST_EXIT_TONE], selectValues, GUEST_EXIT_TONE),
          handleSelectChange(GUEST_EXIT_TONE),
        isDesktop
        )}
      </Box>
      <Box sx={pc.lineBreak} />
      <TextField
        label={translate[GUEST_PASSCODE]}
        sx={[pc.textField, isDesktop ? pc.lineExpander : null, readOnlyFieldSx].filter(Boolean)}
        value={confRestRep[GUEST_PASSCODE] || ""}
        margin="normal"
        slotProps={{
          input: {
            readOnly: true,
          },
        }}
      />
      {selectField(
        customStyle,
        translate[GUEST_CODE_LENGTH],
        getSelectValues(selectValues, PARAM_GUEST_CODE_LENGTH),
        getSelectedValueTranslated(confRestRep[GUEST_CODE_LENGTH], selectValues, PARAM_GUEST_CODE_LENGTH),
        handleSelectChange(PARAM_GUEST_CODE_LENGTH, GUEST_CODE_LENGTH),
      isDesktop
      )}
      <Box sx={pc.lineBreak} />
      {selectField(
        customStyle,
        translate[GUEST_CUSTOM_CONFIRM_MESSAGE],
        getSelectValues(selectValues, PARAM_CUSTOM_CONFIRM_MESSAGE),
        getSelectedValueTranslated(
          confRestRep[GUEST_CUSTOM_CONFIRM_MESSAGE],
          selectValues,
          PARAM_CUSTOM_CONFIRM_MESSAGE
        ),
        handleSelectChange(PARAM_CUSTOM_CONFIRM_MESSAGE, GUEST_CUSTOM_CONFIRM_MESSAGE),
      isDesktop
      )}
      <Box sx={pc.lineBreak} />
      {switchControl(
        pc,
        confRestRep[GUEST_TALK_MODE],
        GUEST_TALK_MODE,
        translate[GUEST_TALK_MODE],
        handleSwitchChange(GUEST_TALK_MODE)
      )}
      <Box sx={pc.lineBreak} />
      {switchControl(
        pc,
        confRestRep[ALLOW_GUEST_MUTE_OVERRIDE],
        ALLOW_GUEST_MUTE_OVERRIDE,
        translate[ALLOW_GUEST_MUTE_OVERRIDE],
        handleSwitchChange(ALLOW_GUEST_MUTE_OVERRIDE)
      )}
      <Box sx={pc.lineBreak} />
    </Box>
  );
};

PMHostAndGuest.propTypes = {
  confRestRep: PropTypes.object.isRequired,
  selectValues: PropTypes.object.isRequired,
  setConfRestRep: PropTypes.func.isRequired,
  translate: PropTypes.object.isRequired,
};

export default PMHostAndGuest;
